/**
 * Safe localStorage access (private mode / blocked storage throws). Keys are
 * namespaced with "l2a:". Use only for per-device conveniences (theme, sidebar,
 * table density, recents) — never for data that must persist or be shared.
 */
const PREFIX = "l2a:";
const listeners = new Set<(key: string) => void>();

export function storageKey(key: string): string {
  return key.startsWith(PREFIX) ? key : `${PREFIX}${key}`;
}

export function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(storageKey(key));
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function writeStorage<T>(key: string, value: T): void {
  const k = storageKey(key);
  try {
    if (value === undefined) localStorage.removeItem(k);
    else localStorage.setItem(k, JSON.stringify(value));
  } catch {
    /* storage unavailable: keep in-memory behaviour only */
  }
  for (const l of listeners) l(k);
}

export function removeStorage(key: string): void {
  writeStorage(key, undefined);
}

/** Subscribe to changes of any key (same tab via writeStorage, other tabs via the storage event). */
export function subscribeStorage(listener: (key: string) => void): () => void {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key) listener(e.key);
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}
